// @ts-nocheck
import axios from 'axios';
import { URL } from '../../service/Service';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/20/solid';
import AuthShell from '../../components/auth/AuthShell';

const getPasswordStrength = (value) => [
  value.length >= 8,
  /[A-Z]/.test(value),
  /[a-z]/.test(value),
  /\d/.test(value),
  /[^A-Za-z0-9]/.test(value),
].filter(Boolean).length;

const RegisterPage = () => {
  const [password, setPassword] = useState('');
  const [userPasswordConfirm, setUserPasswordConfirm] = useState('');
  const [email, setEmail] = useState('');

  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = Boolean(userPasswordConfirm) && password === userPasswordConfirm;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('A palavra-passe deve ter pelo menos 8 caracteres, incluindo maiúscula, minúscula, número e símbolo.');
      return;
    }

    if (password !== userPasswordConfirm) {
      setError('As palavras-passe não coincidem.');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    try {
      setSubmitting(true);
      await axios.post(`${URL}/api/users/createcompanyuser`, {
        password,
        email: normalizedEmail,
      });

      navigate('/login', {
        replace: true,
        state: {
          registrationSuccess: true,
          registeredEmail: normalizedEmail,
        },
      });
    } catch (requestError) {
      const responseMessage = typeof requestError?.response?.data === 'string'
        ? requestError.response.data
        : '';

      if (requestError?.response?.status === 409 || responseMessage === 'Email is already registered') {
        setError('Já existe uma conta registada com este email.');
      } else if (responseMessage === 'The password must be at least 8 characters long and include uppercase, lowercase, number and special character') {
        setError('A palavra-passe não cumpre os requisitos de segurança.');
      } else {
        setError('Não foi possível criar a conta. Confirme os dados e tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Nova conta"
      title="Comece a criar formulários"
      description="Crie a sua conta com email e palavra-passe."
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="ui-label">
            Email
          </label>
          <div className="mt-1">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="ui-input"
              placeholder="nome@empresa.pt"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="ui-label">
            Palavra-passe
          </label>
          <div className="mt-1 relative">
            <input
              id="password"
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="ui-input pr-12"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((visible) => !visible)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-green-cl hover:bg-emerald-50"
              aria-label={passwordVisible ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
            >
              {passwordVisible
                ? <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                : <EyeIcon className="h-5 w-5 text-gray-500" />}
            </button>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Mínimo de 8 caracteres, com maiúscula, minúscula, número e símbolo.
          </p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200" aria-label={`${passwordStrength} de 5 requisitos de segurança cumpridos`}>
            <div
              className={`h-full rounded-full transition-all duration-300 ${passwordStrength === 5 ? 'bg-emerald-600' : 'bg-amber-500'}`}
              style={{ width: `${passwordStrength * 20}%` }}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password-confirmation" className="ui-label">
            Confirmar palavra-passe
          </label>
          <div className="mt-1 relative">
            <input
              id="password-confirmation"
              name="password-confirmation"
              type={confirmPasswordVisible ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={userPasswordConfirm}
              onChange={(event) => setUserPasswordConfirm(event.target.value)}
              className="ui-input pr-12"
            />
            <button
              type="button"
              onClick={() => setConfirmPasswordVisible((visible) => !visible)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-green-cl hover:bg-emerald-50"
              aria-label={confirmPasswordVisible ? 'Ocultar confirmação' : 'Mostrar confirmação'}
            >
              {confirmPasswordVisible
                ? <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                : <EyeIcon className="h-5 w-5 text-gray-500" />}
            </button>
          </div>
          {userPasswordConfirm && (
            <p className={`mt-2 text-xs font-semibold ${passwordsMatch ? 'text-emerald-700' : 'text-rose-600'}`}>
              {passwordsMatch ? 'As palavras-passe coincidem.' : 'As palavras-passe não coincidem.'}
            </p>
          )}
        </div>

        {error && <div className="ui-alert-error text-center">{error}</div>}

        <button
          type="submit"
          disabled={submitting}
          className="ui-button-primary w-full"
        >
          {submitting ? 'A criar conta...' : 'Criar conta'}
        </button>

        <div className="text-center text-sm">
          <a href="/login" className="auth-link">
            Já tem conta? Entrar
          </a>
        </div>
      </form>
    </AuthShell>
  );
};

export default RegisterPage;
